// Dispatcher: the kit core against the signed-in instance (kit.ts; plain HTTP only for what is not GraphQL, net.ts),
// fixtures with BUN_PUBLIC_USE_MOCKS=true. Where to talk to, the bearer and the person live here (set by the auth
// store) so every call carries them; `refresh` renews an expired access token once per 401.
import { indexedDb, type Kit } from "@hq-org/kit-wasm";
import { createPlatformKit } from "@hq-org/platform-wasm";
import { createKitApi } from "./kit";
import { promptTool } from "./prompts";
import { createMockApi, mockKit } from "./mock";
import { createNet, WASM } from "./net";
import type { Api } from "./types";

// Bun inlines BUN_PUBLIC_* that are set at build time; unset ones stay `process.env.X`, which throws in the browser.
const env = (read: () => string | undefined) => {
	try {
		return read();
	} catch {
		return undefined;
	}
};
// The instance serves this web at its own origin (hq:Decision/instance-own-origin): the API is the page's origin.
// BUN_PUBLIC_API_BASE only for the dev server (bun at :3000 against a truss elsewhere).
export const API_BASE_DEFAULT =
	env(() => process.env.BUN_PUBLIC_API_BASE) || (typeof location !== "undefined" ? location.origin : "");
/** Served by the instance itself (not the dev server): sign-in is the instance's passkey pages at /auth. */
export const SAME_ORIGIN = typeof location !== "undefined" && API_BASE_DEFAULT === location.origin;
export const USE_MOCKS = env(() => process.env.BUN_PUBLIC_USE_MOCKS) === "true";

const conn = {
	base: API_BASE_DEFAULT,
	auth: null as string | null,
	token: null as string | null,
	/** the home instance's name and the signed-in person: the kit's store on this device is theirs */
	instance: null as string | null,
	person: null as string | null,
	refresh: (async () => null) as () => Promise<string | null>,
};

/** The instance's API base right now. */
export let API_BASE = conn.base;

let session: { key: string; kit: Promise<Kit> } | null = null;

/** A kit in memory for one question, before the session's person is known (sign-in: who is this token). */
const onceKit = (token: string) => createPlatformKit({ origin: conn.base, token, live: false, wasm: WASM });

/** The session's kit: one per instance and person, its records kept on this device (IndexedDB). */
export async function sessionKit(): Promise<Kit> {
	// the mock preview: the same core over a sample instance (api/mock)
	if (USE_MOCKS) return mockKit(WASM);
	if (!conn.token) throw new Error("signed out");
	if (!conn.person) {
		const k = await onceKit(conn.token);
		try {
			conn.person = (await k.ask<{ id: string }>({ type: "me" })).id;
		} finally {
			k.close();
		}
	}
	const key = `${conn.base}|${conn.instance ?? ""}|${conn.person}`;
	if (session?.key !== key) {
		closeKit();
		// local-first: the kit holds a replica of what this person may see; screens are views over it
		const make = () =>
			createPlatformKit({
				origin: conn.base,
				token: conn.token!,
				person: conn.person!,
				storage: indexedDb(`hq-kit:${conn.instance ?? ""}:${conn.person}`),
				wasm: WASM,
				replica: true,
			});
		// a start the browser aborted (Firefox: "AbortError: The operation was aborted.", a page opened again while it
		// loaded) is tried once more, never shown as the app's error
		const kit = make()
			.catch((e) => (/abort/i.test(`${e?.name} ${e?.message}`) ? make() : Promise.reject(e)))
			.then((k) => {
				k.onUnauthorized(() => void conn.refresh());
				(globalThis as { __hqKit?: unknown }).__hqKit = { kit: k }; // inspection (platform/surfaces/api/check/look.ts, devtools)
				return k;
			});
		session = { key, kit };
		kit.catch(() => session?.kit === kit && (session = null));
	}
	return session.kit;
}

function closeKit() {
	const s = session;
	session = null;
	void s?.kit.then((k) => k.close(), () => {});
}

/** Sign-out: forget this device's records, end the kit. */
export function resetKit() {
	void session?.kit.then((k) => k.reset(), () => {});
	closeKit();
}

export function configure(c: Partial<typeof conn>) {
	const before = conn.token;
	Object.assign(conn, Object.fromEntries(Object.entries(c).filter(([, v]) => v !== undefined)));
	API_BASE = conn.base;
	if (conn.token !== before) void session?.kit.then((k) => k.setToken(conn.token), () => {});
}
export const setToken = (t: string | null) => configure({ token: t });

const net = createNet({ base: () => conn.base, auth: () => conn.auth, token: () => conn.token, refresh: () => conn.refresh() });

export const agentTool = (name: "prompt_render" | "prompt_save" | "graphql", args: Record<string, any>) => name === "graphql"
	? net.agentTool("graphql", args)
	: promptTool((query, variables) => net.agentTool("graphql", { query, variables }), name, args);

export const api: Api = USE_MOCKS
	? createMockApi(150, () => mockKit(WASM))
	: createKitApi({
			kit: sessionKit,
			// before the person is known (sign-in): a kit in memory, for one question
			once: onceKit,
			person: () => conn.person,
			net,
			refresh: () => conn.refresh(),
		});
